import { Checkbox } from '@/components/ui/checkbox';
import { FormField } from '@/components/ui/form';
import { Textarea } from '@/components/ui/textarea';
import { getOriginOfAssetsOptions } from '@/constants/formOptions';
import { useFormsTranslation } from '@/hooks/useTranslation';
import { Shield } from 'lucide-react';
import { useEffect, useMemo } from 'react';
import type {
  Control,
  UseFormGetValues,
  UseFormSetValue,
} from 'react-hook-form';
import { FormFieldWrapper } from '../../FormFieldWrapper';
import type { FormKData } from '../types/formK.types';
import {
  handleCheckboxChange,
  shouldShowOriginOfAssetsOther,
} from '../utils/formK.utils';

interface OriginOfAssetsSectionProps {
  control: Control<FormKData>;
  watchOriginOfAssets?: string[];
  setValue: UseFormSetValue<FormKData>;
  getValues: UseFormGetValues<FormKData>;
}

/**
 * Origin of Assets Section Component
 * Handles multi-select checkboxes for asset origins and conditional "other" text field
 */
export function OriginOfAssetsSection({
  control,
  watchOriginOfAssets,
  setValue,
  getValues,
}: OriginOfAssetsSectionProps) {
  const { t: tForms } = useFormsTranslation();

  // Memoized origin of assets options
  const originOfAssetsOptions = useMemo(
    () => getOriginOfAssetsOptions(tForms),
    [tForms]
  );

  // Check if "other" is selected
  const showOriginOfAssetsOther = useMemo(
    () => shouldShowOriginOfAssetsOther(watchOriginOfAssets),
    [watchOriginOfAssets]
  );

  // Clear "other" text field when "other" checkbox is unchecked
  useEffect(() => {
    if (!showOriginOfAssetsOther && getValues('originOfAssetsOther')) {
      setValue('originOfAssetsOther', '');
    }
  }, [showOriginOfAssetsOther, setValue, getValues]);

  return (
    <section className='space-y-6' aria-labelledby='origin-of-assets-heading'>
      <div className='flex items-center gap-2 mb-4'>
        <Shield className='w-5 h-5' aria-hidden='true' />
        <h3 id='origin-of-assets-heading' className='text-lg font-semibold'>
          {tForms('formK.sections.originOfAssets')}
        </h3>
      </div>

      <div className='space-y-4'>
        <FormField
          control={control}
          name='originOfAssets'
          render={({ field, fieldState }) => (
            <FormFieldWrapper
              label={tForms('formK.fields.originOfAssets')}
              name='originOfAssets'
              tooltip={tForms('formK.tooltips.originOfAssets')}
              error={fieldState.error?.message}
              isGroupLabel={true}
            >
              <div className='space-y-3'>
                {originOfAssetsOptions.map(option => (
                  <div
                    key={option.value}
                    className='flex items-center space-x-2'
                  >
                    <Checkbox
                      id={`origin-${option.value}`}
                      checked={field.value?.includes(option.value) || false}
                      onCheckedChange={checked =>
                        field.onChange(
                          handleCheckboxChange(
                            checked as boolean,
                            option.value,
                            field.value
                          )
                        )
                      }
                    />
                    <label
                      htmlFor={`origin-${option.value}`}
                      className='text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer'
                    >
                      {option.label}
                    </label>
                  </div>
                ))}
              </div>
            </FormFieldWrapper>
          )}
        />

        {/* Conditional "Other" text field */}
        {showOriginOfAssetsOther && (
          <FormField
            control={control}
            name='originOfAssetsOther'
            render={({ field, fieldState }) => (
              <FormFieldWrapper
                label={tForms('formK.fields.originOfAssetsOther')}
                name='originOfAssetsOther'
                tooltip={tForms('formK.tooltips.originOfAssetsOther')}
                required
                error={fieldState.error?.message}
              >
                <Textarea
                  {...field}
                  placeholder={tForms('formK.placeholders.originOfAssetsOther')}
                  className='min-h-[100px] resize-y'
                  rows={3}
                />
              </FormFieldWrapper>
            )}
          />
        )}
      </div>
    </section>
  );
}
